import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { DisabledTooltip } from '@/components/DisabledTooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/Dropdown';
import { Comparison } from '@/components/RunsTable/filter/ast';
import { RunStatsSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { getComparatorLabel } from '../FilterBar.utils';
import { FilterField, StandardFilterField } from '../constants';
import { FilterBarArgumentInput } from './FilterBarArgumentInput';
import { normalizeComparatorArgument } from './utils/normalizeComparatorArgument';

export function FilterFieldInput(props: {
  stats: RunStatsSchema | undefined;
  field: FilterField | undefined;

  value: Partial<Comparison>;
  onChange: (entry: Partial<Comparison>) => void;

  getArgumentValues?: (value: {
    runStats: RunStatsSchema | undefined;
  }) => Array<{ value: string | number | boolean }>;

  isOptional?: boolean;

  updateEveryChange?: boolean;

  readOnly?: boolean;
}) {
  if (!props.field) return null;

  const isReadOnly = props.readOnly ?? false;

  if ('fieldComponent' in props.field) {
    return (
      <props.field.fieldComponent
        value={props.value}
        onChange={(item) => {
          if (isReadOnly) return;
          props.onChange({ ...props.value, ...item });
        }}
        readOnly={isReadOnly}
      />
    );
  }

  const field = props.field as StandardFilterField;
  const isDisabled =
    !!props.field?.disabled?.({ runStats: props.stats }) ||
    props.field.availableComps.length <= 1;

  const comparator = props.field?.availableComps.find(
    (i) => i.id === props.value.comparator
  );

  return (
    <>
      <DropdownMenu
        defaultOpen={
          !props.isOptional && props.value.comparator == null && !isReadOnly
        }
        onOpenChange={() => {
          if (isReadOnly) return;
        }}
      >
        <DropdownMenuTrigger
          className={cn(
            'flex items-center gap-1.5 whitespace-nowrap rounded-md border border-tertiary bg-background px-2 py-1',
            'border-none bg-tertiary hover:bg-quaternary',
            {
              'text-tertiary': props.value.comparator == null,
              'opacity-50 hover:bg-tertiary': isDisabled,
              'opacity-100 hover:bg-tertiary': isReadOnly,
            }
          )}
          disabled={isDisabled || isReadOnly}
        >
          <DisabledTooltip
            disabled={isDisabled}
            disabledMsg={props.field.disabledTooltip}
          >
            <span>
              {comparator
                ? getComparatorLabel(comparator, field.valueType)
                : props.value.comparator ?? 'Op'}
            </span>
          </DisabledTooltip>
          {!isDisabled && !isReadOnly && (
            <ChevronDownIcon className="h-3 w-3 text-tertiary" />
          )}
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent>
            {field.availableComps.map((comp, idx) => (
              <DropdownMenuItem
                key={idx}
                onClick={() => {
                  if (isReadOnly) return;

                  const newArgument = normalizeComparatorArgument(
                    comp.id,
                    props.value.argument
                  );

                  props.onChange({
                    ...props.value,
                    comparator: comp.id,
                    argument: newArgument,
                  });
                }}
                className={'px-2 py-1 text-xxs'}
              >
                {getComparatorLabel(comp, field.valueType)}
              </DropdownMenuItem>
            ))}

            {props.isOptional ? (
              <>
                <DropdownMenuSeparator />

                <DropdownMenuItem
                  className="text-tertiary"
                  onClick={() => {
                    if (isReadOnly) return;
                    props.onChange({
                      ...props.value,
                      comparator: undefined,
                      argument: undefined,
                    });
                  }}
                >
                  Clear
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>

      {(comparator || typeof props.value.argument !== 'undefined') &&
      props.value.comparator !== 'exists' ? (
        <FilterBarArgumentInput
          stats={props.stats}
          field={props.field}
          comparator={comparator}
          value={props.value.argument}
          getFieldFacets={props.getArgumentValues}
          onChange={(argument) => {
            if (isReadOnly) return;
            props.onChange({ ...props.value, argument });
          }}
          updateEveryChange={props.updateEveryChange}
          readOnly={isReadOnly}
        />
      ) : null}
    </>
  );
}
